import { CalendarDays } from "lucide-react";
import { useRef, useState, type ComponentProps } from "react";

import { useLocale, useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { formatDay } from "@/shared/lib/format";
import { controlSizeClassName, inputClassName, type ControlSize } from "@/shared/ui/styles";

type NativeDateProps = Omit<
  ComponentProps<"input">,
  "type" | "value" | "defaultValue" | "onChange" | "min" | "max" | "size" | "placeholder"
>;

interface DateFieldProps extends NativeDateProps {
  /** An ISO date ("2026-10-08"), or null when empty. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** The earliest and latest ISO dates that can be picked. */
  min?: string;
  max?: string;
  /** "sm" (h-8, the default) for property rows, "md" (h-9) next to Input. */
  size?: ControlSize;
  /** What an empty field says; "Pick a date" by default. */
  placeholder?: string;
}

/* Keys that only move focus or close things; any other key means the person is typing a date. */
const NAVIGATION_KEYS = new Set(["Tab", "Shift", "Escape", "Enter"]);

/**
 * A date picker in the Input look, built on the browser's own date field so phones get their
 * native picker. Browsers write that field in their own format ("09/08/2026" is 9 August to an
 * Egyptian reader and 8 September to the browser), so while it rests it shows the date in the
 * interface language instead ("٨ سبتمبر ٢٠٢٦"). A click anywhere opens the calendar; typing
 * shows the browser's segments, as typing a date needs. Clearing it gives null.
 */
export function DateField({
  value,
  onChange,
  size = "sm",
  placeholder,
  className,
  onFocus,
  onBlur,
  onKeyDown,
  onPointerDown,
  onClick,
  ...props
}: DateFieldProps) {
  const t = useT();
  const { locale } = useLocale();
  const [typing, setTyping] = useState(false);
  const pointerDown = useRef(false);
  const shared = cn(inputClassName, controlSizeClassName[size], className);

  return (
    <span className="relative flex min-w-0">
      <input
        type="date"
        value={value ?? ""}
        onChange={(event) => {
          onChange(event.target.value === "" ? null : event.target.value);
        }}
        onPointerDown={(event) => {
          pointerDown.current = true;
          onPointerDown?.(event);
        }}
        onClick={(event) => {
          // Not every browser has showPicker, and it refuses without a user gesture.
          try {
            event.currentTarget.showPicker();
          } catch {
            /* the browser's own calendar button still works */
          }
          onClick?.(event);
        }}
        onFocus={(event) => {
          // Focus from the keyboard (not a click) is someone about to type the date.
          if (!pointerDown.current) setTyping(true);
          pointerDown.current = false;
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setTyping(false);
          onBlur?.(event);
        }}
        onKeyDown={(event) => {
          if (!NAVIGATION_KEYS.has(event.key)) setTyping(true);
          onKeyDown?.(event);
        }}
        className={cn(
          shared,
          "min-w-36 tabular-nums [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-80",
          // At rest the overlay shows the date and its own calendar icon. Chrome draws its button
          // on the right even in Arabic, where it would cover the start of the text.
          !typing &&
            "cursor-pointer text-transparent [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-datetime-edit]:opacity-0",
        )}
        {...props}
      />
      {typing ? null : (
        <span
          aria-hidden="true"
          className={cn(
            shared,
            "pointer-events-none absolute inset-0 flex items-center gap-2 border-transparent bg-transparent shadow-none",
            value === null && "text-ink-subtle",
          )}
        >
          <span className="truncate">
            {value === null ? (placeholder ?? t("date.pick")) : formatDay(value, locale)}
          </span>
          <CalendarDays className="ms-auto size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
